import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiEdit3, FiTrash2, FiMove, FiEye,
  FiSave, FiType, FiImage, FiLink, FiMail
} = FiIcons;

const LandingPageBuilder = () => {
  const { id } = useParams();
  const [blocks, setBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const blockTypes = [
    { type: 'heading', name: 'Heading', icon: FiType },
    { type: 'text', name: 'Text', icon: FiEdit3 },
    { type: 'image', name: 'Image', icon: FiImage },
    { type: 'button', name: 'Button', icon: FiLink },
    { type: 'contact', name: 'Contact Form', icon: FiMail }
  ];

  const addBlock = (type) => {
    const newBlock = {
      id: Date.now().toString(),
      type,
      content: getDefaultContent(type),
      styles: getDefaultStyles(type)
    };
    setBlocks([...blocks, newBlock]);
  };

  const getDefaultContent = (type) => {
    switch (type) {
      case 'heading':
        return { text: 'Your Heading Here' };
      case 'text':
        return { text: 'Your text content goes here...' };
      case 'image':
        return { src: '', alt: 'Image description' };
      case 'button':
        return { text: 'Click Here', url: '#' };
      case 'contact':
        return { title: 'Contact Us', fields: ['name', 'email', 'message'] };
      default:
        return {};
    }
  };

  const getDefaultStyles = (type) => {
    return {
      backgroundColor: 'transparent',
      textColor: '#000000',
      fontSize: type === 'heading' ? '2rem' : '1rem',
      textAlign: 'center',
      padding: '1rem',
      marginBottom: '1rem'
    };
  };

  const updateBlock = (id, updates) => {
    setBlocks(blocks.map(block => 
      block.id === id ? { ...block, ...updates } : block
    ));
  };

  const deleteBlock = (id) => {
    setBlocks(blocks.filter(block => block.id !== id));
    setSelectedBlock(null);
  };

  const renderBlock = (block) => {
    const blockStyles = {
      backgroundColor: block.styles.backgroundColor,
      color: block.styles.textColor,
      fontSize: block.styles.fontSize,
      textAlign: block.styles.textAlign,
      padding: block.styles.padding,
      marginBottom: block.styles.marginBottom
    };

    switch (block.type) {
      case 'heading':
        return (
          <h1 style={blockStyles}>
            {block.content.text}
          </h1>
        );
      case 'text':
        return (
          <p style={blockStyles}>
            {block.content.text}
          </p>
        );
      case 'image':
        return (
          <div style={blockStyles}>
            {block.content.src ? (
              <img 
                src={block.content.src} 
                alt={block.content.alt}
                className="max-w-full h-auto"
              />
            ) : (
              <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
        );
      case 'button':
        return (
          <div style={{ ...blockStyles, display: 'flex', justifyContent: blockStyles.textAlign }}>
            <button 
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              style={{ color: blockStyles.textColor }}
            >
              {block.content.text}
            </button>
          </div>
        );
      case 'contact':
        return (
          <div style={blockStyles}>
            <h3 className="text-xl font-semibold mb-4">{block.content.title}</h3>
            <form className="space-y-4">
              {block.content.fields.includes('name') && (
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                />
              )}
              {block.content.fields.includes('email') && (
                <input
                  type="email"
                  placeholder="Your Email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                />
              )}
              {block.content.fields.includes('message') && (
                <textarea
                  placeholder="Your Message"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                />
              )}
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Send Message
              </button>
            </form>
          </div>
        );
      default:
        return null;
    }
  };

  const renderBlockEditor = () => {
    if (!selectedBlock) return null;

    const block = blocks.find(b => b.id === selectedBlock);
    if (!block) return null;

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Edit {block.type.charAt(0).toUpperCase() + block.type.slice(1)}
        </h3>
        
        <div className="space-y-4">
          {/* Content Editor */}
          {block.type === 'heading' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heading Text
              </label>
              <input
                type="text"
                value={block.content.text}
                onChange={(e) => updateBlock(block.id, {
                  content: { ...block.content, text: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {block.type === 'text' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Content
              </label>
              <textarea
                value={block.content.text}
                onChange={(e) => updateBlock(block.id, {
                  content: { ...block.content, text: e.target.value }
                })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {block.type === 'button' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={block.content.text}
                  onChange={(e) => updateBlock(block.id, {
                    content: { ...block.content, text: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button URL
                </label>
                <input
                  type="url"
                  value={block.content.url}
                  onChange={(e) => updateBlock(block.id, {
                    content: { ...block.content, url: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* Style Editor */}
          <div className="pt-4 border-t border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Styling</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Color
                </label>
                <input
                  type="color"
                  value={block.styles.textColor}
                  onChange={(e) => updateBlock(block.id, {
                    styles: { ...block.styles, textColor: e.target.value }
                  })}
                  className="w-full h-10 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <input
                  type="color"
                  value={block.styles.backgroundColor}
                  onChange={(e) => updateBlock(block.id, {
                    styles: { ...block.styles, backgroundColor: e.target.value }
                  })}
                  className="w-full h-10 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Align
                </label>
                <select
                  value={block.styles.textAlign}
                  onChange={(e) => updateBlock(block.id, {
                    styles: { ...block.styles, textAlign: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font Size
                </label>
                <select
                  value={block.styles.fontSize}
                  onChange={(e) => updateBlock(block.id, {
                    styles: { ...block.styles, fontSize: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="0.875rem">Small</option>
                  <option value="1rem">Normal</option>
                  <option value="1.25rem">Large</option>
                  <option value="1.5rem">XL</option>
                  <option value="2rem">2XL</option>
                  <option value="3rem">3XL</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              onClick={() => deleteBlock(block.id)}
              className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (previewMode) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Preview Header */}
        <div className="bg-white shadow-sm border-b p-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">Landing Page Preview</h1>
            <button
              onClick={() => setPreviewMode(false)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Exit Preview
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="max-w-2xl mx-auto py-8 px-4">
          {blocks.map((block) => (
            <div key={block.id}>
              {renderBlock(block)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Landing Page Builder</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create beautiful landing pages for your QR codes
          </p>
        </div>
        
        <div className="mt-3 sm:mt-0 flex items-center space-x-3">
          <button
            onClick={() => setPreviewMode(true)}
            className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            <SafeIcon icon={FiEye} className="w-4 h-4 mr-2" />
            Preview
          </button>
          
          <button className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
            <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
            Save Page
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Block Palette */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Blocks</h3>
            
            <div className="space-y-2">
              {blockTypes.map((blockType) => (
                <button
                  key={blockType.type}
                  onClick={() => addBlock(blockType.type)}
                  className="w-full flex items-center p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={blockType.icon} className="w-5 h-5 text-gray-600 mr-3" />
                  <span className="font-medium text-gray-900">{blockType.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Block Editor */}
          {selectedBlock && (
            <div className="mt-6">
              {renderBlockEditor()}
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 min-h-[600px]">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Canvas</h3>
            </div>
            
            <div className="p-6">
              {blocks.length > 0 ? (
                <div className="space-y-4">
                  {blocks.map((block, index) => (
                    <motion.div
                      key={block.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`relative group border-2 border-dashed transition-colors ${
                        selectedBlock === block.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedBlock(block.id)}
                    >
                      {renderBlock(block)}
                      
                      {/* Block Controls */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex items-center space-x-1 bg-white rounded-lg shadow-lg p-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBlock(block.id);
                            }}
                            className="p-1 text-gray-600 hover:text-gray-800 rounded"
                          >
                            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBlock(block.id);
                            }}
                            className="p-1 text-red-600 hover:text-red-800 rounded"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <SafeIcon icon={FiPlus} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Start Building Your Page
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Add blocks from the sidebar to create your landing page
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageBuilder;